import React from "react";
import { Link } from "react-router-dom";
import { useMeta } from "@/lib/seo";

const TINYTIM_ID = "user_4aa6fa409549";

const COPY = {
  en: {
    intro: { h: "Why Soul Radar?", p: [
      "Soul Radar started with a simple idea: there should be one place where you can see what's happening across the Soul scene.",
      "Finding the next nighter, alldayer or weekender shouldn't mean searching through countless social media posts, groups and flyers. I wanted something independent, built specifically for the scene – with the events at the centre.",
    ] },
    next: { h: "Where's the next nighter?", lead: "That's the question behind Soul Radar.", p: [
      "See what's coming up, discover events near you or further away, save the ones you don't want to miss and add them straight to your calendar. Soul Radar is designed to help you keep track of what's on and plan the night – or the whole trip.",
      "That's also why event listings include the details that actually matter: dates, venues, DJs, tickets and, where available, hotel tips for those travelling from further away.",
    ] },
    family: { h: "Built for the Soul Family", p: [
      "Soul Radar comes from a love of Northern Soul and is built for the people who keep the scene alive – dancers, DJs, collectors, promoters and everyone travelling miles for a great night.",
      "From local soul nights to international weekenders, the idea remains the same: bring the events together and make them easier to find.",
      "Independent of the big social media platforms. Focused on the music, the events and the people who care about them.",
    ] },
    org: { h: "For Organizers & DJs", p: [
      "Organizing a Soul event? List it on Soul Radar and reach people who are actually looking for Soul events. Event listings are free and designed to provide the information visitors need to decide, plan and travel.",
      "DJs can also create their identity on Soul Radar, connect their appearances to events and be followed by people who want to know where they're playing next.",
      "Found an event that's missing? Invite the organizer to add it.",
    ], cta: "Submit your event →" },
    faith: { h: "Keep the faith.", p: ["Soul Radar is built for the Soul Family. Worldwide.", "Have fun exploring, find your next nighter, and plan your next trip."], bye: "See you on the dancefloor," },
  },
  de: {
    intro: { h: "Warum Soul Radar?", p: [
      "Soul Radar ist aus einer einfachen Idee entstanden: Es sollte einen Ort geben, an dem man sieht, was in der Soul-Szene los ist.",
      "Den nächsten Nighter, Alldayer oder Weekender zu finden, sollte nicht bedeuten, sich durch unzählige Social-Media-Posts, Gruppen und Flyer zu wühlen. Ich wollte etwas Unabhängiges, eigens für die Szene gebaut – mit den Events im Mittelpunkt.",
    ] },
    next: { h: "Wo ist der nächste Nighter?", lead: "Das ist die Frage hinter Soul Radar.", p: [
      "Sieh, was ansteht, entdecke Events in deiner Nähe oder weiter weg, speichere die, die du nicht verpassen willst, und übernimm sie direkt in deinen Kalender. Soul Radar hilft dir, den Überblick zu behalten und die Nacht zu planen – oder gleich die ganze Reise.",
      "Deshalb enthalten die Event-Einträge auch die Details, die wirklich zählen: Termine, Locations, DJs, Tickets und – wo vorhanden – Hoteltipps für alle, die von weiter her anreisen.",
    ] },
    family: { h: "Gebaut für die Soul Family", p: [
      "Soul Radar kommt aus der Liebe zum Northern Soul und ist für die Menschen gemacht, die die Szene lebendig halten – Tänzer, DJs, Sammler, Veranstalter und alle, die für eine großartige Nacht viele Kilometer fahren.",
      "Von der lokalen Soul Night bis zum internationalen Weekender bleibt die Idee dieselbe: die Events zusammenbringen und leichter auffindbar machen.",
      "Unabhängig von den großen Social-Media-Plattformen. Fokussiert auf die Musik, die Events und die Menschen, denen sie am Herzen liegen.",
    ] },
    org: { h: "Für Veranstalter & DJs", p: [
      "Du veranstaltest ein Soul-Event? Trag es auf Soul Radar ein und erreiche Leute, die wirklich nach Soul-Events suchen. Einträge sind kostenlos und so aufgebaut, dass Besucher alles finden, um zu entscheiden, zu planen und anzureisen.",
      "Auch DJs können auf Soul Radar ihre Identität anlegen, ihre Auftritte mit Events verknüpfen und von Leuten gefolgt werden, die wissen wollen, wo sie als Nächstes spielen.",
      "Ein Event fehlt? Lade den Veranstalter ein, es einzutragen.",
    ], cta: "Event eintragen →" },
    faith: { h: "Keep the faith.", p: ["Soul Radar ist für die Soul Family gebaut. Weltweit.", "Viel Spaß beim Entdecken, finde deinen nächsten Nighter und plane deine nächste Reise."], bye: "Wir sehen uns auf dem Dancefloor," },
  },
};

const Sec = ({ title, children, testid }) => (
  <section className="border-t border-foreground pt-8 mt-12" data-testid={testid}>
    <h2 className="font-display uppercase tracking-tighter text-3xl sm:text-4xl leading-none mb-5">{title}</h2>
    <div className="space-y-4 text-base sm:text-lg leading-relaxed max-w-2xl">{children}</div>
  </section>
);

export default function About() {
  const c = COPY.en;
  useMeta({ title: "About", description: "Soul Radar is the independent international event calendar for the Soul scene – nighters, alldayers, weekenders, live shows and record fairs.", path: "/about" });
  return (
    <article className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-16" data-testid="about-page">
      <div className="eyebrow text-primary">Soul Radar</div>
      <h1 className="font-display uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3" data-testid="about-title">{c.intro.h}</h1>
      <div className="mt-6 space-y-4 text-base sm:text-lg leading-relaxed max-w-2xl">{c.intro.p.map((x, i) => <p key={i}>{x}</p>)}</div>

      <Sec title={c.next.h} testid="about-next-nighter">
        <p className="text-primary font-display uppercase text-xl tracking-tight">{c.next.lead}</p>
        {c.next.p.map((x, i) => <p key={i}>{x}</p>)}
      </Sec>

      <Sec title={c.family.h} testid="about-family">{c.family.p.map((x, i) => <p key={i}>{x}</p>)}</Sec>

      <Sec title={c.org.h} testid="about-organizers">
        {c.org.p.map((x, i) => <p key={i}>{x}</p>)}
        <Link to="/submit" className="inline-flex items-center gap-2 mt-2 font-display uppercase tracking-wide text-lg border-b-2 border-primary hover:text-primary transition-colors" data-testid="about-submit-cta">{c.org.cta}</Link>
      </Sec>

      <Sec title={c.faith.h} testid="about-faith">
        {c.faith.p.map((x, i) => <p key={i}>{x}</p>)}
        <p className="pt-4">{c.faith.bye}<br />
          <Link to={`/users/${TINYTIM_ID}`} className="font-display uppercase text-2xl tracking-tight hover:text-primary transition-colors" data-testid="about-tinytim-link">TinyTim →</Link>
        </p>
      </Sec>
    </article>
  );
}
